/**
 * kb-doc-edit-dialog.tsx — "Sửa toàn văn" một TÀI LIỆU (bài viết) trong kho: tiêu đề, nội dung (tải bản đầy đủ khi danh
 * sách chỉ có đoạn đầu), thư mục, từ khoá. Đổi nội dung → chia đoạn lại từ đầu và AI học lại cả tài liệu
 * (muốn sửa một chỗ nhỏ: dùng "Sửa" trên từng đoạn ở trình đọc — chỉ đoạn đó học lại).
 */
import { useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Loader2, Save } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { apiError } from '@/lib/api-client'
import { embedStatusInfo, sourceLine } from '@/lib/kb-status'
import { useUpdateKnowledgeEntry, fetchKnowledgeEntry, type KnowledgeEntry } from '@/hooks/use-knowledge'
import { KbCategorySelect } from './kb-category-select'

export function KbDocEditDialog({ entry, canEdit, onClose }: { entry: KnowledgeEntry; canEdit: boolean; onClose: () => void }) {
  const { t } = useTranslation('aiKnow')
  const update = useUpdateKnowledgeEntry()
  const [title, setTitle] = useState(entry.title ?? '')
  const [content, setContent] = useState(entry.contentTruncated ? '' : entry.content)
  const [loadingFull, setLoadingFull] = useState(!!entry.contentTruncated)
  const [categoryId, setCategoryId] = useState(entry.categoryId ?? '')
  const [keywords, setKeywords] = useState(entry.keywords ?? '')

  useEffect(() => {
    if (!entry.contentTruncated) return
    let alive = true
    fetchKnowledgeEntry(entry.id)
      .then((full) => { if (alive) setContent(full.content) })
      .catch((err) => toast.error(apiError(err)))
      .finally(() => { if (alive) setLoadingFull(false) })
    return () => { alive = false }
  }, [entry.id, entry.contentTruncated])

  async function save() {
    try {
      await update.mutateAsync({
        id: entry.id,
        data: {
          title: title.trim() || null, content, keywords: keywords.trim() || null,
          categoryId: categoryId || null, changeNote: 'Sửa từ Kho tri thức', // i18n-ignore: ghi chú phiên bản lưu vào dữ liệu
        },
      })
      toast.success(t('docEdit.saved'))
      onClose()
    } catch (err) {
      toast.error(apiError(err))
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('docEdit.title')}</DialogTitle>
          <DialogDescription>
            {embedStatusInfo(entry).label}{sourceLine(entry.sourceMeta) ? ` · ${sourceLine(entry.sourceMeta)}` : ''}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {entry.chunksEdited && (
            <p className="rounded-md border border-warning/40 bg-warning/5 px-3 py-2 text-xs">
              <Trans t={t} i18nKey="docEdit.chunksEditedNote" components={{ b: <b /> }} />
            </p>
          )}
          <div>
            <Label>{t('docEdit.nameLabel')}</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>
          <div>
            <Label>{t('docEdit.contentLabel')}</Label>
            {loadingFull ? (
              <p className="flex items-center gap-2 py-6 text-xs text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> {t('docEdit.loadingFull')}</p>
            ) : (
              <textarea className="min-h-56 w-full rounded-md border bg-transparent p-2 font-mono text-xs outline-none" value={content} onChange={(e) => setContent(e.target.value)} />
            )}
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            <div>
              <Label>{t('docEdit.folderLabel')}</Label>
              <KbCategorySelect knowledgeBaseId={entry.knowledgeBaseId} kind="article" value={categoryId} onChange={setCategoryId} noneLabel={t('docEdit.noFolder')} />
            </div>
            <div>
              <Label>{t('docEdit.keywordsLabel')}</Label>
              <Input value={keywords} onChange={(e) => setKeywords(e.target.value)} placeholder={t('docEdit.keywordsPlaceholder')} />
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>{t('common:actions.close')}</Button>
          {canEdit && (
            <Button onClick={() => void save()} disabled={update.isPending || loadingFull || (!!entry.contentTruncated && !content)}>
              {update.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} {t('common:actions.save')}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
